import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useServices } from '@/contexts/app-services';
import type { Block, TaskBlock } from '@/entities/chat-message';
import type { CheckInResponseCreateInput } from '@/entities/check-in-response';
import type { Period } from '@/entities/types';
import { useUpsertCheckInResponses } from '@/queries/check-in-response';
import { ensurePlanCached, useUpdatePlan } from '@/queries/plan';
import { useCreateGoalsForPlan } from '@/queries/planned-goal';
import { logError } from '@/services/logger';
import { getWeekStartAt, toMidnightLocalString } from '@/utils/datetime';

interface SubmitCheckInArgs {
  planId: string;
  period: Period;
  startAtLocal: string;
  targetStartAt: Date;
  responses: CheckInResponseCreateInput;
  insights: Block[];
}

function insightsToGoals(blocks: Block[]) {
  return blocks
    .filter((b): b is TaskBlock => b.type === 'entity')
    .map((b) => ({ title: b.data.title }));
}

export function useSubmitCheckIn() {
  const { api } = useServices();
  const queryClient = useQueryClient();
  const upsertResponses = useUpsertCheckInResponses();
  const createGoalsForPlan = useCreateGoalsForPlan();
  const updatePlan = useUpdatePlan();

  const [isPending, setIsPending] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submit = useCallback(
    async ({
      planId,
      period,
      startAtLocal,
      targetStartAt,
      responses,
      insights,
    }: SubmitCheckInArgs) => {
      setIsPending(true);
      setIsSuccess(false);
      setError(null);

      try {
        await upsertResponses.mutateAsync({ planId, responses });

        const entityInsights = insights.filter((b) => b.type === 'entity');
        const weekInsights = entityInsights.filter(
          (b) => b.data.period === 'week',
        );
        const dayInsights = entityInsights.filter(
          (b) => b.data.period !== 'week',
        );

        if (dayInsights.length > 0) {
          try {
            await createGoalsForPlan.mutateAsync({
              planId,
              goals: insightsToGoals(dayInsights),
              period,
              startAtLocal,
            });
          } catch (err) {
            logError(err, 'Failed to create day goals');
          }
        }

        if (weekInsights.length > 0) {
          try {
            const weekStartAt = getWeekStartAt(targetStartAt);
            const weekStartAtLocal = toMidnightLocalString(weekStartAt);
            const weekPlan = await ensurePlanCached(
              queryClient,
              api.plans,
              'week',
              weekStartAt,
              weekStartAtLocal,
            );

            await createGoalsForPlan.mutateAsync({
              planId: weekPlan.id,
              goals: insightsToGoals(weekInsights),
              period: 'week',
              startAtLocal: weekStartAtLocal,
            });
          } catch (err) {
            logError(err, 'Failed to create week goals');
          }
        }

        await updatePlan.mutateAsync({
          planId,
          period,
          startAtLocal,
          update: { status: 'checked-in' },
        });

        setIsSuccess(true);
      } catch (err) {
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setIsPending(false);
      }
    },
    [api, queryClient, upsertResponses, createGoalsForPlan, updatePlan],
  );

  return { submit, isPending, isSuccess, error };
}
